import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { ActivityIcon, BusThreeIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import { useForm, useWatch } from 'react-hook-form'

import { useNativeCountryField } from '../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  LeaderFreeRateEnum,
  leaderFreeRateEnumMapping
} from '../../../../lib/types/enums/activity/leader-free-rate.enum'
import {
  TransportModeEnum,
  transportModeEnumMapping
} from '../../../../lib/types/enums/activity/transport-mode.enum'
import {
  CurrencyEnum,
  currencyEnumMapping
} from '../../../../lib/types/enums/common/currency.enum'
import { ActivityInput } from '../../../../lib/types/models/ol/activity/activity-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import { ControlledAdressInput } from '../../../elements/inputs/address-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { ACTIVITY_FORM_SCHEMA } from '../utils'

type Props = BaseFormProps<ActivityInput, true> & {
  currency: CurrencyEnum
}

const ActivityForm: React.FC<Props> = ({ currency, defaultValues, ...validationButtonProps }) => {
  const { t } = useTranslate()

  const form = useForm<ActivityInput>({
    resolver: zodResolver(ACTIVITY_FORM_SCHEMA(t)),
    defaultValues,
    mode: FORM_MODE
  })
  useNativeCountryField(form, 'country')

  const {
    activitySplitAge: activityAge,
    transportSplitAge,
    transportMode
  } = useWatch({ control: form.control })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <Box className="flex flex-col gap-4">
          <InnerBox title="Activity" className="rounded-xl flex flex-col gap-3 ">
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormControl>
                      <ControlledInput required {...field} label={'Activity Name'} type={'text'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locationName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        required
                        {...field}
                        label={'Name of the location'}
                        type={'text'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledPhoneInput
                        {...field}
                        label={'Phone number'}
                        defaultCountry="FR"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledAdressInput
                        nativeCountryNames
                        {...field}
                        required
                        label="Address"
                        onDetailsChange={(placeDetails) => {
                          form.setValue('address1', placeDetails.address, { shouldValidate: true })
                          form.setValue('zipCode', placeDetails.postalCode, {
                            shouldValidate: true
                          })
                          form.setValue('city', placeDetails.city, { shouldValidate: true })
                          form.setValue('country', placeDetails.country, { shouldValidate: true })
                          form.trigger(['address1', 'zipCode', 'city', 'country'])
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address2"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput {...field} label={'Address complement'} type={'text'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput required {...field} label={'Town'} type={'text'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput required {...field} label={'Zip code'} type={'text'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormControl>
                      <ControlledInput required {...field} label={'Country'} type={'text'} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <Box className="bg-white-hover border border-grey-light flex flex-col gap-3">
              <Badge
                icon={<ActivityIcon color={tailwindColors.secondary.DEFAULT} />}
                text="Activity costs"
                size="medium"
                textColor={tailwindColors.secondary.DEFAULT}
                iconPosition="start"
              />
              <div />
              <FormField
                control={form.control}
                name="activityLeaderFreeRatio"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        options={Object.values(LeaderFreeRateEnum).map((rate) => ({
                          label: t(leaderFreeRateEnumMapping[rate]),
                          value: rate
                        }))}
                        label={'Leader Free Ratio'}
                        labelClassName="bg-white-hover"
                        selectedOption={getSelectedEnumOption(
                          field.value,
                          leaderFreeRateEnumMapping,
                          t
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-4 gap-3">
                <FormField
                  control={form.control}
                  name="activitySplitAge"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          labelClassName="bg-white-hover"
                          label={'Split age (for prices)'}
                          type={'number'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="activityChildCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          labelClassName="bg-white-hover"
                          icon={currencyEnumMapping[currency].icon}
                          label={`cost/student under ${activityAge ?? 'X'}`}
                          type={'number'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="activityAdultCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          icon={currencyEnumMapping[currency].icon}
                          labelClassName="bg-white-hover"
                          label={`cost/student over ${activityAge ?? 'X'}`}
                          type={'number'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="activityLeaderCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          icon={currencyEnumMapping[currency].icon}
                          labelClassName="bg-white-hover"
                          label={'cost/leader'}
                          type={'number'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="activityFlatGroupCost"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        icon={currencyEnumMapping[currency].icon}
                        labelClassName="bg-white-hover"
                        label={'Flat cost/group'}
                        type={'number'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Box>
            <Box className="bg-white-hover border border-grey-light flex flex-col gap-3">
              <Badge
                icon={<BusThreeIcon color={tailwindColors.secondary.DEFAULT} />}
                text="Transport costs"
                size="medium"
                textColor={tailwindColors.secondary.DEFAULT}
                iconPosition="start"
              />
              <div />
              <FormField
                control={form.control}
                name="transportMode"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        options={Object.values(TransportModeEnum).map((mode) => ({
                          label: t(transportModeEnumMapping[mode]),
                          value: mode
                        }))}
                        label={'Transport mode'}
                        labelClassName="bg-white-hover"
                        selectedOption={getSelectedEnumOption(
                          field.value,
                          transportModeEnumMapping,
                          t
                        )}
                        onChange={(value) => {
                          field.onChange(value)
                          if (value !== TransportModeEnum.EXTRA_BUS_OR_TRAIN) {
                            form.setValue('transportSplitAge', null)
                            form.setValue('transportChildCost', null)
                            form.setValue('transportAdultCost', null)
                            form.setValue('transportLeaderCost', null)
                            form.setValue('transportFlatGroupCost', null)
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {transportMode === TransportModeEnum.EXTRA_BUS_OR_TRAIN && (
                <>
                  <div className="grid grid-cols-4 gap-3">
                    <FormField
                      control={form.control}
                      name="transportSplitAge"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledInput
                              {...field}
                              onChange={(value) => field.onChange(value)}
                              labelClassName="bg-white-hover"
                              label="Split age (for prices)"
                              type="number"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="transportChildCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledInput
                              {...field}
                              onChange={(value) => field.onChange(value)}
                              icon={currencyEnumMapping[currency].icon}
                              labelClassName="bg-white-hover"
                              label={`cost/student under ${transportSplitAge ?? 'X'}`}
                              type="number"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="transportAdultCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledInput
                              {...field}
                              onChange={(value) => field.onChange(value)}
                              icon={currencyEnumMapping[currency].icon}
                              labelClassName="bg-white-hover"
                              label={`cost/student over ${transportSplitAge ?? 'X'}`}
                              type="number"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="transportLeaderCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledInput
                              {...field}
                              onChange={(value) => field.onChange(value)}
                              icon={currencyEnumMapping[currency].icon}
                              labelClassName="bg-white-hover"
                              label="cost/leader"
                              type="number"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="transportFlatGroupCost"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledInput
                            {...field}
                            onChange={(value) => field.onChange(value)}
                            icon={currencyEnumMapping[currency].icon}
                            labelClassName="bg-white-hover"
                            label="Flat cost/group"
                            type="number"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </>
              )}
            </Box>
          </InnerBox>
          <InnerBox title="Comments" className="rounded-xl">
            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormControl>
                  <ControlledInput {...field} label="Comments" type="textarea" />
                </FormControl>
              )}
            />
          </InnerBox>
          <ValidationButtons {...validationButtonProps} />
        </Box>
      </form>
    </Form>
  )
}

export default ActivityForm
